/**
 * This file is intentionally not exported from composables/index.ts
 * The inclusion of Vue components in this file would necessitate
 * a fair bit of extra setup in unit tests, so by excl
 */
import { computed, defineAsyncComponent, h, ref } from 'vue';
import { useActivities } from './useActivities';
import { useFetch } from './useFetch';
import { useUtilities } from './useUtilities';
import { useCrmRelationshipsStore } from '@apparatix/stores/useCrmRelationshipsStore';
import type { ActivityClickHandlerOptions, SupportedActivitiesView } from './useActivities';
import { Relationship } from '@apparatix/schemas/Relationship';
import type { Activity, ActivityModel, ActivityTypeName } from '@apparatix/schemas/Activities';
import { QuillTemplateJson } from '@apparatix/leads/activities/QuillTemplateJson';
import { MenuItem } from 'primevue/menuitem';
const Modal = defineAsyncComponent(() => import('@apparatix/Components/Modal.vue'));
const CreateOrEdit = defineAsyncComponent(() => import('@apparatix/leads/activities/CreateOrEdit.vue'));

export const useCreateActivity = (saveNoteAsLegacyStickyNote?: boolean) => {
  const {
    addActivityClickHandler,
    confirmDeleteActivity,
    createOrUpdateActivity,
    deleteActivity,
    getDisplayType,
    getIconForActivityType,
    isEmailActivity,
    modalFieldConfig,
    modalTitle,
  } = useActivities();

  const { post } = useFetch();
  const { arrayHasValues } = useUtilities();

  const relationshipStore = useCrmRelationshipsStore();

  const modalVisible = ref(false);
  const model = ref<ActivityModel>();
  const modelChanges = ref<Partial<ActivityModel>>();
  const currentView = ref<SupportedActivitiesView>();
  const relationships = ref(relationshipStore.initializeRelationshipsRef());
  const isLoading = ref(false);
  const shouldSaveLegacyStickyNote = ref(
    typeof saveNoteAsLegacyStickyNote === 'boolean' ? saveNoteAsLegacyStickyNote : false,
  );

  const getMobileMenuItems = (allowedTypes: ActivityTypeName[] = ['Note', 'Reminder', 'Task'], view?: SupportedActivitiesView, parent?: Relationship): MenuItem[] => [{
    label: 'Activities',
    items: allowedTypes.map(type => ({
      label: `Add ${getDisplayType(type).toLowerCase()}`,
      icon: getIconForActivityType(type),
      command: () => showModal(type, view, parent),
    })),
  }];

  const setupModel = (provided: ActivityModel, changes: Partial<ActivityModel>) => {
    model.value = provided;
    modelChanges.value = changes;
    modalVisible.value = true;
  };

  const showModal = (type: ActivityTypeName, view?: SupportedActivitiesView, parentObject?: Relationship, editActivity?: Activity) => {
    const options: ActivityClickHandlerOptions = {
      parentObject: { ...parentObject, isParent: true },
      type,
      view,
    };

    if (editActivity?.type?.name) {
      options.type = editActivity.type.name as ActivityTypeName;
    }

    if (view && parentObject?.id) {
      relationshipStore.getRelationships(view, parentObject.id, true)
        .then(available => {
          relationships.value = available;
          addActivityClickHandler(options, setupModel, editActivity);
        });
    } else {
      addActivityClickHandler(options, setupModel, editActivity);
    }
  };

  const onModalClose = () => {
    modalVisible.value = false;
    model.value = undefined;
    modelChanges.value = undefined;
  };

  const onCreateOrUpdateActivity = async (series: boolean) => {
    isLoading.value = true;
    modelChanges.value.newFiles = [...model.value.newFiles];

    if (series) {
      modelChanges.value.rebuildFuture = true;
    }

    const activity = await createOrUpdateActivity(modelChanges.value);

    if (shouldSaveLegacyStickyNote.value && activity?.type?.name === 'Note' && arrayHasValues(activity?.notes)) {
      try {
        const [note] = activity.notes;
        if (note.templateJson && note.templateJson !== 'null') {
          const template: QuillTemplateJson = JSON.parse(note.templateJson);
          const text = template.ops?.reduce((acc, current) => `${acc}${current.insert}`, '');

          await post<string>('/util/widget/stickyNoteNew.php', { text }, true);
        }
      } catch {
        // Not really anything to do here probably
      }
    }

    isLoading.value = false;
    onModalClose();
  };

  const acceptDeleteActivity = async (series: boolean, id: number) => {
    isLoading.value = true;
    if (series) {
      await deleteActivity(id, true);
    } else {
      await deleteActivity(id);
    }
    isLoading.value = false;
    if (modalVisible.value) {
      onModalClose();
    }
  };

  const onCancel = () => {
    if (isLoading.value) return;
    onModalClose();
  };

  const onDeleteActivity = async (id: number, series: boolean) => confirmDeleteActivity(series, () => acceptDeleteActivity(series, id));

  const addActivityComponent = computed(() => h(
    Modal,
    {
      width: 'w-screen md:w-[90vw] lg:w-[720px]',
      title: modalTitle.value,
      visible: modalVisible.value,
      disableAccept: true,
      disableCancel: true,
      disableFooter: true,
      onClose: onCancel,
    },
    {
      body: () => h(
        CreateOrEdit,
        {
          'modelValue': model.value,
          'onUpdate:modelValue': (newValue: ActivityModel) => model.value = newValue,
          'readonly': isEmailActivity(model.value) ? false : Boolean(model.value.completedAt),
          'isLoading': isLoading.value,
          'fields': modalFieldConfig.value,
          'currentView': currentView.value,
          'relationships': relationships.value,
          'onSave': onCreateOrUpdateActivity,
          'onDelete': onDeleteActivity,
          'onCancel': onCancel,
        },
      ),
    },
  ));

  return {
    addActivityComponent,
    getMobileMenuItems,
    showModal,
  };
};
